<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { useEventListener } from '@vueuse/core';
  import {
    clampSheetDrag,
    sheetHeightPx,
    SHEET_DRAG_THRESHOLD_PX,
  } from '@/components/pos-v2/ui/pos-ui-sheet.utils';
  import { setAssistantInset } from '@/composition/assistant/assistant-inset';
  import { assistantPill } from '@/composition/assistant/assistant-pill';
  import { useAssistantStore } from '@/composition/assistant/use-assistant-store';
  import { useGpthelpScope } from '@/composition/assistant/use-gpthelp-scope';
  import { useAuthStore } from '@/composition/use-auth';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { snapAssistantSheet } from '@/domain/assistant/assistant-panel-tier';
  import { ASSISTANT_GA } from '@/domain/assistant/assistant-rollout';
  import {
    createAdminHostAdapter,
    warmAdminHostContext,
  } from '@/gpthelp/gpthelp-host-adapter';
  import type {
    IGhBusyChangeDetail,
    IGhHostEventName,
    IGhReadyDetail,
  } from '@/gpthelp/gpthelp-host-contract';
  import {
    ensureGpthelpDefined,
    gpthelpChatbotId,
  } from '@/gpthelp/gpthelp-loader';
  import { isGpthelpSupportedBrowser } from '@/gpthelp/gpthelp-support';
  import { GPTHELP_THEME_VARS } from '@/gpthelp/gpthelp-theme';
  import { deepActiveElement } from '@/lib/dom/deep-active-element';
  import { initialLanguageLoaded } from '@/plugins/i18n';
  import V2AssistantPanelState from './V2AssistantPanelState.vue';

  /**
   * The «Asistente» panel (plan §9.2): one always-mounted `<aside>` whose
   * geometry follows the tier — a 400 px right sidebar that pushes the page
   * (registered in Vuetify's layout, like the left drawer), a 420 px overlay
   * drawer or a 92 % / 40 % bottom sheet. `<gpt-help>` is created on the
   * first open, after the script and the stored language loaded, and is then
   * never removed or re-parented: tiers and visibility only change classes.
   */

  /** Vuetify layout uid of the docked panel (never a Vue uid). */
  const ASSISTANT_LAYOUT_UID = -1;

  const store = useAssistantStore();
  const authStore = useAuthStore();
  const vm = getCurrentInstance()!.proxy;
  const { height: stableHeight } = useStableWindowSize();
  const { scopeKey, scopeLabel } = useGpthelpScope();
  const adapter = createAdminHostAdapter();
  const chatbotId = gpthelpChatbotId();
  const themeVars = GPTHELP_THEME_VARS;

  const panel = ref<HTMLElement | null>(null);
  const widget = ref<HTMLElement | null>(null);
  /** Flips once, when the first open has loaded the widget. */
  const elementCreated = ref(false);
  /** The sheet height while a drag runs; null otherwise. */
  const dragPx = ref<number | null>(null);

  /** Where the focus was when the panel opened. */
  let opener: HTMLElement | null = null;
  /** The focus was inside the panel when it started to close. */
  let focusWasInside = false;
  let drag: {
    pointerId: number;
    startY: number;
    startPx: number;
    moved: boolean;
  } | null = null;
  let disposed = false;

  const isModal = computed(() => store.tier !== 'docked');
  const panelStyle = computed(() => {
    if (store.tier !== 'sheet') return undefined;
    const px =
      dragPx.value ?? sheetHeightPx(store.sheetHeight, stableHeight.value);
    return px > 0 ? { height: `${px}px` } : undefined;
  });

  //#region Widget
  function close() {
    store.closePanel();
  }

  /** Dispatches a host event on `<gpt-help>`; false while it does not exist. */
  function dispatchToWidget(name: IGhHostEventName): boolean {
    if (!widget.value) return false;
    widget.value.dispatchEvent(new CustomEvent(name));
    return true;
  }

  /** The first detail item of a widget event (Vue custom elements emit an array). */
  function eventDetail(event: Event): unknown {
    return event instanceof CustomEvent && Array.isArray(event.detail)
      ? event.detail[0]
      : undefined;
  }

  function isBusyChange(value: unknown): value is IGhBusyChangeDetail {
    return (
      typeof value === 'object' &&
      value !== null &&
      'busy' in value &&
      typeof value.busy === 'boolean'
    );
  }

  function isReady(value: unknown): value is IGhReadyDetail {
    return (
      typeof value === 'object' &&
      value !== null &&
      'protocolVersion' in value &&
      typeof value.protocolVersion === 'number'
    );
  }

  function onBusy(event: Event) {
    const detail = eventDetail(event);
    store.setBusy(isBusyChange(detail) && detail.busy);
  }

  function onReady(event: Event) {
    const detail = eventDetail(event);
    store.setProtocolVersion(isReady(detail) ? detail.protocolVersion : null);
    // First open: the aside held the focus while the widget loaded.
    if (store.shown && hasFinePointer() && containsFocus())
      dispatchToWidget('gpthelp:focus-composer');
  }

  /**
   * Loads the widget once (the first open, or «Reintentar»). The element is
   * created only after the stored language loaded, so it never starts in
   * English.
   */
  async function ensureWidget() {
    if (elementCreated.value || store.status === 'loading') return;
    if (!isGpthelpSupportedBrowser()) {
      store.setStatus('unsupported');
      return;
    }
    // «Reintentar» leaves with the error state: the panel keeps the focus
    // (Esc, the Tab trap and the composer focus on `ready` need it inside).
    if (containsFocus()) panel.value?.focus({ preventScroll: true });
    store.setStatus('loading');
    try {
      await Promise.all([ensureGpthelpDefined(), initialLanguageLoaded]);
    } catch (error) {
      if (disposed) return;
      console.warn('[assistant] Failed to load the widget', error);
      store.setStatus('error');
      return;
    }
    if (disposed) return;
    elementCreated.value = true;
    store.setStatus('ready');
  }

  // The welcome (and the widget's strings) re-read on every context change
  // the panel owns: the admin language, the store and branch, and the writes
  // config, which may arrive after an open welcome showed the read-only body.
  // The element reads its new `locale` / `scope-key` attributes on the next
  // tick. Route changes are dispatched by V2AssistantRoot.
  watch([() => vm.$i18n.locale, scopeKey, () => store.config], () => {
    void nextTick(() => dispatchToWidget('gpthelp:context-changed'));
  });

  // Another user while the panel stays mounted: the widget drops the previous
  // user's chat too (plan §9.11). The store already reset the rest; at logout
  // the panel unmounts with the element.
  watch(
    () => authStore.user?.id ?? null,
    (id, previous) => {
      if (previous !== null && id !== previous)
        dispatchToWidget('gpthelp:reset');
    }
  );

  // Warm the business-day cache for the widget's next send.
  watch(
    [scopeKey, elementCreated],
    ([key, created]) => {
      if (key && created) warmAdminHostContext();
    },
    { immediate: true }
  );
  //#endregion

  //#region Focus
  function hasFinePointer(): boolean {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: fine)').matches
    );
  }

  /**
   * Whether the focus is inside the panel. The page's `activeElement` is
   * the right node here: a field focused inside `<gpt-help>` reports its
   * shadow host, which is a light-DOM child of the aside (`contains` never
   * crosses into shadow roots).
   */
  function containsFocus(): boolean {
    const active = document.activeElement;
    return !!panel.value && !!active && panel.value.contains(active);
  }

  function isVisible(element: HTMLElement | null): element is HTMLElement {
    return (
      !!element && element.isConnected && element.getClientRects().length > 0
    );
  }

  /**
   * Ctrl/⌘+K with the focus outside the open panel: to the composer, or to
   * the panel while the widget is not there yet.
   */
  function focusComposer() {
    if (!dispatchToWidget('gpthelp:focus-composer'))
      panel.value?.focus({ preventScroll: true });
  }

  // Before the DOM changes: remember the opener, or let go of the focus
  // before hiding (a focused field would keep the phone keyboard up).
  watch(
    () => store.shown,
    (shown) => {
      if (shown) {
        const active = deepActiveElement();
        opener =
          active instanceof HTMLElement &&
          active !== document.body &&
          !containsFocus()
            ? active
            : null;
        void ensureWidget();
        return;
      }
      focusWasInside = containsFocus();
      if (focusWasInside) {
        const active = deepActiveElement();
        if (active instanceof HTMLElement) active.blur();
      }
      cancelDrag();
    }
  );

  // After the DOM changed: focus the composer (fine pointers) or the panel
  // (touch: the keyboard must not jump up); on close, a focus that was
  // inside goes to the pill (plan §9.2), or to the opener when the pill is
  // gone (a route without the panel).
  watch(
    () => store.shown,
    (shown) => {
      void nextTick(() => {
        if (disposed || store.shown !== shown) return;
        if (shown) {
          if (!(hasFinePointer() && dispatchToWidget('gpthelp:focus-composer')))
            panel.value?.focus({ preventScroll: true });
          return;
        }
        const target = [assistantPill.value, opener].find(isVisible);
        opener = null;
        if (focusWasInside && target) target.focus({ preventScroll: true });
        focusWasInside = false;
      });
    },
    { flush: 'post' }
  );

  /**
   * Keeps Tab inside a modal tier while the panel shows its own states:
   * the widget traps it itself (`modal`), but only once it exists.
   */
  function trapTab(event: KeyboardEvent) {
    const root = panel.value;
    if (!root) return;
    const buttons = Array.from(root.querySelectorAll('button')).filter(
      (button) => !button.disabled && isVisible(button)
    );
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (!first || !last) {
      event.preventDefault();
      root.focus({ preventScroll: true });
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === root)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * Esc closes the panel, unless the widget handles it (it stops the ones it
   * uses); Tab stays inside the modal tiers.
   */
  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      if (!elementCreated.value && isModal.value) trapTab(event);
      return;
    }
    if (event.key !== 'Escape') return;
    if (widget.value && event.composedPath().includes(widget.value)) return;
    event.stopPropagation();
    close();
  }

  // The left nav opening closes an overlay or sheet (one layer at a time).
  // V2ShellDrawer signals every opening: the menu button, a swipe, a resize.
  useEventListener(document, 'restaurantMenuOpened', () => {
    if (store.shown && store.tier !== 'docked') close();
  });
  //#endregion

  //#region Sheet drag
  function onPointerDown(event: PointerEvent) {
    if (store.tier !== 'sheet' || !panel.value) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    drag = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startPx: panel.value.getBoundingClientRect().height,
      moved: false,
    };
    const handle = event.currentTarget;
    if (handle instanceof HTMLElement)
      handle.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: PointerEvent) {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const delta = drag.startY - event.clientY;
    if (!drag.moved && Math.abs(delta) < SHEET_DRAG_THRESHOLD_PX) return;
    drag.moved = true;
    dragPx.value = clampSheetDrag(drag.startPx + delta, stableHeight.value);
  }

  function onPointerUp(event: PointerEvent) {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const moved = drag.moved;
    const px = dragPx.value;
    cancelDrag();
    if (!moved || px === null) return;
    const next = snapAssistantSheet(px, stableHeight.value);
    if (next === null) close();
    else store.setSheetHeight(next);
  }

  function cancelDrag() {
    drag = null;
    dragPx.value = null;
  }

  watch(() => store.tier, cancelDrag);
  //#endregion

  //#region Layout
  /** The docked panel pads `v-main` like the left drawer, and tells fixed page UI its width. */
  function applyInset(px: number) {
    const application = vm.$vuetify.application;
    if (px > 0) application.register(ASSISTANT_LAYOUT_UID, 'right', px);
    else application.unregister(ASSISTANT_LAYOUT_UID, 'right');
    document.documentElement.style.setProperty(
      '--assistant-inset-right',
      `${px}px`
    );
    setAssistantInset(px);
  }

  watch(() => store.insetRight, applyInset, { immediate: true });

  /**
   * The permanent left drawer's width while the panel is docked (0 px
   * otherwise). The page is then narrow enough for its sheets and fixed bars,
   * which span the viewport, to open; the drawer paints over the page, so
   * they start where the page does (`--assistant-inset-left`).
   */
  const insetLeft = computed(() =>
    store.insetRight > 0 ? vm.$vuetify.application.left : 0
  );

  function applyInsetLeft(px: number) {
    document.documentElement.style.setProperty(
      '--assistant-inset-left',
      `${px}px`
    );
  }

  watch(insetLeft, applyInsetLeft, { immediate: true });
  //#endregion

  // Mounted while already open (a remount between stores): load right away.
  if (store.shown) void ensureWidget();

  // The panel leaves with its store (or at logout): it never reopens by itself.
  onBeforeUnmount(() => {
    disposed = true;
    cancelDrag();
    opener = null;
    applyInset(0);
    applyInsetLeft(0);
    store.closePanel();
    store.setBusy(false);
    store.setProtocolVersion(null);
    store.setStatus('idle');
  });

  defineExpose({ dispatchToWidget, containsFocus, focusComposer });
</script>

<template>
  <div class="v2-assistant hidden-print-only">
    <!-- mousedown.prevent: the focus stays inside until close() runs, so it
         can go back to the opener. -->
    <div
      v-show="store.shown && isModal"
      class="assistant-scrim"
      :class="{
        'is-light': store.tier === 'sheet' && store.sheetHeight === 'sm',
      }"
      aria-hidden="true"
      @mousedown.prevent
      @click="close"
    />
    <aside
      v-show="store.shown"
      ref="panel"
      class="v2-assistant-panel"
      :class="[
        `is-${store.tier}`,
        {
          'is-sm': store.sheetHeight === 'sm',
          'is-dragging': dragPx !== null,
        },
      ]"
      :style="panelStyle"
      :aria-label="$t('assistant.panel.label')"
      :role="store.shown && isModal ? 'dialog' : undefined"
      :aria-modal="store.shown && isModal ? 'true' : undefined"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <div
        v-show="store.tier === 'sheet'"
        class="sheet-handle"
        aria-hidden="true"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="cancelDrag"
      >
        <span class="handle-bar" />
      </div>
      <V2AssistantPanelState
        v-if="!elementCreated"
        :status="store.status"
        :tier="store.tier"
        :locale="$i18n.locale"
        :scope-label="scopeLabel"
        @close="close"
        @retry="ensureWidget"
      />
      <gpt-help
        v-if="elementCreated"
        key="widget"
        ref="widget"
        class="assistant-widget"
        mode="panel"
        theme="light"
        :id="chatbotId"
        :scope-key="scopeKey"
        :scope-label="scopeLabel"
        :locale="$i18n.locale"
        :panel-title="$t('assistant.panel.label')"
        :title-badge="ASSISTANT_GA ? null : $t('assistant.panel.beta')"
        :close-button="store.tier === 'sheet' ? 'text' : 'icon'"
        :modal="isModal ? '' : null"
        :style="themeVars"
        :hostAdapter.prop="adapter"
        @close="close"
        @busy-change="onBusy"
        @ready="onReady"
      />
    </aside>
  </div>
</template>

<style scoped lang="scss">
  // Below the 48 px top bar, which stays usable (the pill toggles the panel).
  $bar-height: 48px;

  .v2-assistant {
    .assistant-scrim {
      @apply tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-bg-scrim/40;
      top: $bar-height;
      z-index: 7;
      transition: background-color 180ms ease;

      &.is-light {
        @apply tw-bg-scrim/[0.12];
      }
    }

    .v2-assistant-panel {
      @apply tw-fixed tw-flex tw-flex-col tw-bg-surface tw-outline-none;

      // Pushes the page: Vuetify pads `v-main` by the same 400 px.
      &.is-docked {
        @apply tw-bottom-0 tw-right-0 tw-border-0 tw-border-l tw-border-solid tw-border-neutral-200;
        top: $bar-height;
        width: 400px;
        z-index: 6;
      }

      // Over the page, always leaving 56 px of it on the left.
      &.is-overlay {
        @apply tw-bottom-0 tw-right-0 tw-shadow-lg;
        top: $bar-height;
        width: 420px;
        max-width: calc(100vw - 56px);
        z-index: 8;
        animation: assistant-slide-in-x 180ms ease-out;
      }

      // Bottom sheet; its height comes from the stable window height. No
      // overflow clip: the handle's grab area reaches above the top edge
      // (the content starts below the rounded corners anyway).
      &.is-sheet {
        @apply tw-bottom-0 tw-left-0 tw-right-0 tw-rounded-t-2xl tw-shadow-lg;
        height: 92%;
        max-height: calc(100% - 56px);
        padding-bottom: env(safe-area-inset-bottom);
        z-index: 8;
        transition: height 180ms ease;
        animation: assistant-slide-in-y 180ms ease-out;

        &.is-dragging {
          transition: none;
        }
      }

      .sheet-handle {
        @apply tw-relative tw-flex tw-h-5 tw-shrink-0 tw-cursor-grab tw-items-center tw-justify-center;
        touch-action: none;

        // A 44 px grab area around the 20 px strip (touch first): 10 px up
        // into the scrim (the sheet's top is ≥ 56 px, the top bar's buttons
        // end at 46 px) and 14 px down into the header's middle, where it
        // covers at most the top 6 px of a header button, above its icon.
        // The × or «Cerrar» at the header's end stays clear.
        &::before {
          @apply tw-absolute tw-left-0 tw-right-0 tw-mx-auto;
          content: '';
          top: -10px;
          width: 160px;
          height: 44px;
          z-index: 1;
        }

        .handle-bar {
          @apply tw-h-1 tw-w-10 tw-rounded-full tw-bg-neutral-300;
        }
      }

      .assistant-widget {
        @apply tw-block tw-min-h-0 tw-w-full tw-flex-1;
      }
    }
  }

  @keyframes assistant-slide-in-x {
    from {
      transform: translateX(24px);
      opacity: 0;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }

  @keyframes assistant-slide-in-y {
    from {
      transform: translateY(24px);
      opacity: 0;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-assistant {
      .assistant-scrim,
      .v2-assistant-panel.is-sheet {
        transition: none;
      }

      .v2-assistant-panel.is-overlay,
      .v2-assistant-panel.is-sheet {
        animation: none;
      }
    }
  }
</style>
